import type { NavigationButton, MenuItem } from '@core/ui/navigation';
import type { Filter } from 'contexts/preferences/listing-params/types';
import type { PartialDeep } from 'type-fest';
import { ROOT_PATH_SUFFIX } from 'data/addresses/go-search/types';
import getTargetedUrl from 'utils/targeted-url-helper';
import { DATE_DESCENDING_SORT, DEFAULT_LISTING_PARAMS_FILTER_SHARED } from 'contexts/preferences/listing-params';
import { getOrigin, isClientSide } from 'utils/host-config';
import type { TenantId } from 'tenants/types/tenant';

import type { ResolvedLocationData } from 'hooks/use-location-resolution';

/**
 * Type guard to check if an item has a filter property.
 */
function hasFilterProperty(item: NavigationButton | MenuItem): item is (NavigationButton | MenuItem) & { filter?: PartialDeep<Filter> } {
  return 'filter' in item;
}

/**
 * Builds a URL using getTargetedUrl with the provided filter.
 * For neighbourhood URLs, uses locationData.fullUrlPath.
 * For city-level URLs, constructs the URL from the baseUrl.
 * 
 * @param baseUrl - The base URL path
 * @param locationData - The resolved location data
 * @param filter - Optional filter to apply
 * @param tenantId - Optional tenant ID to use tenant's host for origin (server-side)
 */
function buildUrlWithFilters(
  baseUrl: string,
  locationData: ResolvedLocationData,
  filter?: PartialDeep<Filter>,
  tenantId?: TenantId
): string {
  if (!filter) {
    return baseUrl;
  }

  // Merge with default filters to ensure all required fields are present
  const completeFilter: Filter = {
    ...DEFAULT_LISTING_PARAMS_FILTER_SHARED,
    ...filter,
    slug: locationData.citySlug,
    areaName: locationData.cityName,
    latitude: locationData.coordinates.latitude,
    longitude: locationData.coordinates.longitude,
  } as Filter;

  try {
    // Use fullUrlPath for neighbourhood URLs, otherwise construct from filter's slug
    const urlPath = locationData.isNeighbourhood && locationData.fullUrlPath
      ? locationData.fullUrlPath
      : baseUrl || `/${completeFilter.slug}-${ROOT_PATH_SUFFIX}`;
    
    // Construct full URL from the path
    // getOrigin() automatically handles client/server contexts and uses tenant host if provided
    const url = new URL(urlPath, getOrigin(tenantId));
    const targetedUrl = getTargetedUrl({
      url: url.toString(),
      filters: completeFilter,
      sort: DATE_DESCENDING_SORT,
      pageNumber: 1,
      fallbackSlug: locationData.citySlug,
      rootPathSuffix: ROOT_PATH_SUFFIX,
    });

    return targetedUrl.pathname + targetedUrl.search;
  } catch (error) {
    // If URL construction fails, return the base URL
    if (isClientSide()) { // Only log warnings on client side
      console.warn('Failed to build targeted URL:', error);
    }
    return baseUrl;
  }
}

/**
 * Process navigation items by replacing templates with actual location data
 * This utility processes navigation items before passing them to navigation components
 * 
 * @param items - The navigation items to process
 * @param locationData - The resolved location data
 * @param tenantId - Optional tenant ID to use tenant's host for origin (server-side)
 */
export function processNavigationItemsWithLocation<T extends NavigationButton[] | MenuItem[]>(
  items: T, 
  locationData?: ResolvedLocationData,
  tenantId?: TenantId
): T {
  if (!locationData) {
    return items;
  }
  
  const rawCityName = locationData.cityName || 'City';
  const citySlug = locationData.citySlug || 'search';
  
  // Use clean city name for display (strip province/state)
  const displayCityName = rawCityName.split(',')[0].trim();

  return items.map(item => {
    if ('buttonVariant' in item && item.buttonVariant === 'divider') {
      return item;
    }
    if ('type' in item && item.type === 'divider') {
      return item;
    }
    
    // Resolve the label: if there's a template, replace {city} with the actual city name
    // Otherwise, use the static label
    const resolvedLabel = item.template 
      ? item.template.replace('{city}', displayCityName)
      : item.label || '';
    
    let href = item.href || '';
    
    // Check if item has a filter property
    const itemFilter = hasFilterProperty(item) ? item.filter : undefined;
    
    // If item has a filter, use getTargetedUrl to build the URL from the filter's slug
    if (itemFilter) {
      // buildUrlWithFilters will construct the URL from the filter's slug
      href = buildUrlWithFilters(href, locationData, itemFilter, tenantId);
    } else {
      // For items without filters, use simple template replacement and add suffix if needed
      href = href
        .replace('{city-slug}', citySlug)
        .replace('{city}', displayCityName)
        .replace('{site-location}', locationData.siteLocation.toLowerCase());
      
      // Add -real-estate suffix if it's a city slug path and doesn't already have it
      if (href.startsWith(`/${citySlug}`) && !href.includes(`-${ROOT_PATH_SUFFIX}`)) {
        href = href.replace(`/${citySlug}`, `/${citySlug}-${ROOT_PATH_SUFFIX}`);
      }
    }
    
    return {
      ...item,
      label: resolvedLabel,
      href,
      items: item.items ? processNavigationItemsWithLocation(item.items, locationData, tenantId) : undefined,
    };
  }) as T;
}

